import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import {
  Button,
  Form,
  FormGroup,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import { injectIntl } from 'react-intl';

import {
  deleteCollectionFromCollectionsList,
  setSelectedCollections,
  setSelectedAllCollections,
  hideCollectionMoveModal,
} from '../../actions/Collections';

import {
  APIgetCollection,
  APImoveCollections,
} from '../../api/Collections';

import {
  userCan,
  userIsAdmin,
} from '../../helpers/Auth';
import {
  getCurrentCollectionFilters,
  getParentCollection,
} from '../../helpers/Collections';
import { eventDispatch } from '../../helpers/Events';
import { addFlashMessage } from '../../helpers/FlashMessages';
import { getSetting } from '../../helpers/Settings';

import CollectionSelectCollectionMessage from './SelectCollectionMessage';
import CollectionsTree from './Tree';
import ProcessingProgress from '../ProcessingProgress';
import ProcessingSpinner from '../ProcessingSpinner';

class CollectionsMoveModal extends Component {
  constructor(props) {
    super(props);

    this.state = this.getInitialState();

    this.handleKeyPress = this.handleKeyPress.bind(this);
    this.handleSaveCollections = this.handleSaveCollections.bind(this);
    this.handleAddNewCollection = this.handleAddNewCollection.bind(this);
    this.handleClose = this.handleClose.bind(this);
  }

  getInitialState() {
    const { collections } = this.props;
    const parentCollection = getParentCollection(collections[0]);

    return {
      parentCollectionUuid: parentCollection ? parentCollection.uuid : null,
      progressGroupUuid: null,
      saving: false,
    };
  }

  getProgress() {
    const { backendActions } = this.props;
    const { progressGroupUuid } = this.state;

    return backendActions[`progress_${progressGroupUuid}`] || { total: 0, done: 0 };
  }

  handleKeyPress(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      this.handleSaveCollection();
    }
  }

  handleSaveCollections() {
    const {
      intl,
      afterMoveCollection,
      deleteCollectionFromList,
      unselectAllCollections,
      collections,
      selectedAllCollections,
    } = this.props;
    const { parentCollectionUuid } = this.state;

    this.setState({ saving: true });

    const collectionsToMove = selectedAllCollections ? [] : collections.filter(c => (userCan('collection.edit', c)));
    this.setState({ progressGroupUuid: uuidv4() }, () => {
      const { progressGroupUuid } = this.state;
      APImoveCollections({
        parentCollectionUuid,
        collections: collectionsToMove.map(c => (c.uuid)),
        selectedAllCollections,
        filters: selectedAllCollections ? getCurrentCollectionFilters() : undefined,
        progressGroupUuid,
      }).then(() => {
        addFlashMessage({
          type: 'success',
          text: intl.formatMessage({ defaultMessage: 'Collections have been moved' }),
          button: {
            url: `/collections/${parentCollectionUuid === -1 ? 'my' : parentCollectionUuid}`,
            text: intl.formatMessage({ defaultMessage: 'Open collection' }),
          },
        });

        collectionsToMove.forEach((collection) => {
          deleteCollectionFromList(collection);
          eventDispatch('collectionUpdated', collection);
          eventDispatch('collectionUpdatedTree', collection);
        });

        if (!(
          !parentCollectionUuid ||
          parentCollectionUuid === '-1' ||
          parentCollectionUuid === 'all'
        )) {
          APIgetCollection(parentCollectionUuid).then((response) => {
            eventDispatch('collectionUpdated', response.data);
            eventDispatch('collectionUpdatedTree', response.data);
          });
        }

        afterMoveCollection();
        unselectAllCollections();
        this.handleClose();
      });
    });
  }

  handleAddNewCollection(collectionUuid) {
    this.setState({ parentCollectionUuid: collectionUuid });
  }

  handleClose() {
    const { closeCollectionMoveModal } = this.props;

    this.setState(this.getInitialState());
    closeCollectionMoveModal();
  }

  render() {
    const {
      parentCollectionUuid,
      saving,
    } = this.state;
    const {
      intl,
      collectionMoveModal,
      collections,
      collectionsTotal,
      selectedAllCollections,
    } = this.props;

    const collectionsToMove = selectedAllCollections ? collectionsTotal : collections.filter(c => (userCan('collection.edit', c))).length;

    return (
      <Modal
        isOpen={collectionMoveModal}
        toggle={this.handleClose}
        className="modal-move-collections"
      >
        <ModalHeader toggle={this.handleClose} className="modal-header-center">
          {intl.formatMessage({
            defaultMessage: 'Move {count} {count, plural, one {Collection} other {Collections}}',
          }, { count: collectionsToMove })}
        </ModalHeader>
        <ModalBody>
          <Form
            className="move-collections-form"
            onKeyPress={this.handleKeyPress}
          >
            <FormGroup>
              <CollectionSelectCollectionMessage />
              <CollectionsTree
                selectedCollections={parentCollectionUuid ? [parentCollectionUuid] : ['all']}
                disabledCollections={collections.map(c => (c.uuid))}
                onlyMyCollections={getSetting('cs1') && !userIsAdmin()}
                onlyOpenCollections={true}
                enableAllCollections={true}
                addNewDisabled={saving}
                onChange={(selectedUuids) => {
                  if (selectedUuids.length > 0) {
                    this.setState({ parentCollectionUuid: selectedUuids[0] });
                  } else {
                    this.setState({ parentCollectionUuid: null });
                  }
                }}
                smartCollectionsRestrict={false}
              />
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <div className="buttons-group">
            <Button
              color="secondary"
              disabled={saving}
              onClick={this.handleClose}
            >
              {intl.formatMessage({ defaultMessage: 'Cancel' })}
            </Button>
            <Button
              color="primary"
              disabled={saving || !parentCollectionUuid}
              onClick={this.handleSaveCollections}
            >
              <ProcessingSpinner show={saving} />
              {intl.formatMessage({ defaultMessage: 'Move' })}
              <ProcessingProgress
                show={saving}
                progress={this.getProgress()}
              />
            </Button>
          </div>
        </ModalFooter>
      </Modal>
    );
  }
}

const mapStateToProps = state => ({
  collectionMoveModal: state.Collections.moveModalOpen,
  collectionsTotal: state.Collections.collectionsTotal,
  selectedAllCollections: state.Collections.selectedAllCollections,
  backendActions: state.App.backendActions,
});

const mapDispatchToProps = dispatch => ({
  deleteCollectionFromList: (collection) => {
    dispatch(deleteCollectionFromCollectionsList(collection));
  },
  unselectAllCollections: () => {
    dispatch(setSelectedCollections([]));
    dispatch(setSelectedAllCollections(false));
  },
  closeCollectionMoveModal: () => {
    dispatch(hideCollectionMoveModal());
  },
});

CollectionsMoveModal.propTypes = {
  intl: PropTypes.object.isRequired,
  collectionMoveModal: PropTypes.bool.isRequired,
  collectionsTotal: PropTypes.number.isRequired,
  selectedAllCollections: PropTypes.bool.isRequired,
  backendActions: PropTypes.object.isRequired,
  collections: PropTypes.array.isRequired,
  closeCollectionMoveModal: PropTypes.func.isRequired,
  deleteCollectionFromList: PropTypes.func.isRequired,
  unselectAllCollections: PropTypes.func.isRequired,
  afterMoveCollection: PropTypes.func,
};

CollectionsMoveModal.defaultProps = {
  afterMoveCollection: () => {},
};

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(CollectionsMoveModal));
